import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Show } from "@clerk/react";
import { useT, useSeo, fmtMoney } from "@/lib/i18n";
import { pub, useApi, errMsg } from "@/lib/api";
import { PageHead, HaloCard, Notice, ShareBar } from "@/components/bits";
import { useConfig } from "@/components/Layout";
import { OracleRequest, YtCard, useX } from "@/components/extras";

function useProduct() {
  const [p, setP] = useState(null);
  useEffect(() => { pub.get("/public/products").then((r) => setP(r.data[0] || null)).catch(() => {}); }, []);
  return p;
}

const GALLERY = [
  ["oracle-andreea-waterfall", "Andreea holding the Global Ascension Oracle by a waterfall"], ["oracle-andreea-garden", "Andreea with the oracle in a tropical garden"],
  ["oracle-andreea-sunset", "Andreea with the oracle by the sea at sunset"], ["oracle-andreea-studio", "Andreea with the oracle box in a warm studio"],
  ["oracle-catifea", "The oracle box on velvet"], ["oracle-halou", "The oracle box with a soft halo"], ["oracle-lumina", "The oracle box by candlelight"], ["oracle-piatra", "The oracle box on a stone"],
  ["oracle-setul-deschis", "The open set with the first card"], ["oracle-fetele-cartilor", "Oracle cards fanned out"], ["oracle-ghidul-ilustrat", "The illustrated multilingual guidebook"],
  ["oracle-pachetul-de-carti", "The full 77-card deck"], ["oracle-cutia-in-ambalaj", "The oracle box in its shipping carton"],
];

function OracleGallery() {
  return (
    <section className="wrap pb-16" data-testid="oracle-gallery">
      <div className="columns-2 md:columns-3 lg:columns-4 gap-4">{GALLERY.map(([f, alt]) => (
        <img key={f} src={`/assets/v2/${f}.webp`} alt={alt} loading="lazy" className="w-full rounded-2xl mb-4 break-inside-avoid hover:scale-[1.02] transition-transform duration-500" />))}</div>
    </section>
  );
}

export function Oracle() {
  const { t, tl, pick, lp, locale } = useT();
  useSeo("oracle");
  const p = useProduct();
  const [email, setEmail] = useState("");
  const [listed, setListed] = useState(false);
  const stock = tl("stockStates");
  const x = useX();
  const join = async (e) => { e.preventDefault(); await pub.post("/public/restock", { email, product_id: p.id, locale }); setListed(true); };
  return (
    <>
      <PageHead eyebrow={t("oracleEyebrow")} title="Global Ascension Oracle" lead={x("oracleH")} />
      <OracleGallery />
      <section className="wrap grid md:grid-cols-2 gap-14 pb-16">
        <HaloCard className="card p-6 self-start"><img src="/assets/v2/oracle-studio.webp" alt="Photograph of the Global Ascension Oracle box" className="w-full rounded-2xl" /></HaloCard>
        <div className="grid gap-6 content-start">
          <p className="text-lg">{x("oracleIntro")}</p>
          <div><h2 className="text-3xl">{t("insideH")}</h2><p className="mt-2 text-sm" data-testid="oracle-package">{p?.package_confirmed ? pick(p.package_i18n) : `${t("packagePending")}${pick(p?.package_i18n)}`}</p></div>
          {p && (p.purchasable ? (
            <div className="card p-6 grid gap-3" data-testid="oracle-buy"><p className="serif text-3xl" data-testid="oracle-price">{fmtMoney(p.price_minor, p.currency, locale)}</p><p className="text-sm">{x("shipSep")}</p>
              <Link to={lp("/cart")} className="btn btn-gold" data-testid="oracle-add-cart" onClick={() => localStorage.setItem("aa_cart", JSON.stringify({ product_id: p.id, quantity: 1 }))}>{t("addCart")}</Link></div>
          ) : (
            <div className="card p-6 grid gap-3" data-testid="oracle-unavailable">
              <p className="font-semibold">{p.in_stock ? "" : stock[1]} — {t("priceNotPublished")}</p>
              {listed ? <p role="status" data-testid="restock-ok">{stock[4]}</p> : (
                <form onSubmit={join} className="flex flex-wrap gap-2"><label className="sr-only" htmlFor="restock-email">Email</label><input id="restock-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="flex-1 border rounded-full px-4 min-h-[44px]" placeholder="email@…" data-testid="restock-email" /><button className="btn btn-forest btn-sm" data-testid="restock-submit">{stock[3]}</button></form>)}
              <p className="help">{t("emailOptIn").split(".")[0]} — restock only.</p>
              <Link to={lp("/contact")} className="btn btn-ghost btn-sm justify-self-start">{t("askSet")}</Link>
            </div>))}
          <OracleRequest />
          <YtCard id="IhbxMy6TUGo" title="Global Ascension Oracle — unboxing" />
          <div><h2 className="text-3xl">{t("ritualH")}</h2><ol className="grid gap-3 mt-4">{tl("ritual").map((r, k) => <li key={k} className="flex gap-3"><span className="serif text-2xl text-[var(--gold)]">{k + 1}</span>{r}</li>)}</ol></div>
          <ShareBar path="/oracle" text="Global Ascension Oracle — Ayahuasca Angel" />
        </div>
      </section>
    </>
  );
}

export function Cart() {
  const { t, tl, pick, locale } = useT();
  const api = useApi();
  const cfg = useConfig();
  const p = useProduct();
  const [cart, setCart] = useState(() => { try { return JSON.parse(localStorage.getItem("aa_cart")); } catch { return null; } });
  const [msg, setMsg] = useState("");
  const L = tl("checkoutLabels");
  const x = useX();
  if (!cart || !p || !p.purchasable) return <section className="wrap section"><h1 className="text-5xl">{tl("utility")[3]}</h1><p className="mt-6" data-testid="cart-empty">{t("cartEmpty")}</p></section>;
  const checkout = async () => {
    try { const o = await api.post("/orders", cart); const r = await api.post("/checkout/session", { kind: "order", id: o.id, origin_url: window.location.origin }); localStorage.removeItem("aa_cart"); window.location.href = r.checkout_url; }
    catch (e) { setMsg(errMsg(e, t("payUnavailable"))); }
  };
  return (
    <section className="wrap section max-w-[720px]">
      <h1 className="text-5xl">{tl("utility")[3]}</h1>
      {cfg.checkout_mode === "test" && <Notice>{t("testBanner")}</Notice>}
      <div className="card p-6 mt-6 grid gap-4">
        <p className="serif text-2xl">{pick(p.title_i18n)}</p>
        <label className="field">{L[0]}<select value={cart.quantity} onChange={(e) => { const c = { ...cart, quantity: +e.target.value }; setCart(c); localStorage.setItem("aa_cart", JSON.stringify(c)); }} data-testid="cart-qty">{[1, 2, 3, 4, 5].map((n) => <option key={n}>{n}</option>)}</select></label>
        <p>{L[5]}: {fmtMoney(p.price_minor * cart.quantity, p.currency, locale)}</p>
        <p className="help" data-testid="cart-shipping-note">{x("shipSep")}. {t("taxNote")}</p>
        <p className="help">{t("orderPrivacy")}</p>
        {msg && <p className="error-text" role="alert">{msg}</p>}
        <Show when="signed-in"><button className="btn btn-gold" onClick={checkout} data-testid="cart-pay">{L[9]}</button></Show>
        <Show when="signed-out"><Link to="/sign-in" className="btn btn-gold">{tl("utility")[0]}</Link></Show>
      </div>
    </section>
  );
}
